// LoyaltyContext — single source of truth for /api/loyalty/status.
//
// Polls on mount + every 90s while focused. When the backend reports the
// 2nd wing has been earned AND we previously had wings===1, we trigger
// the SecondWingCelebration (one-time, persisted in localStorage so a
// reload doesn't re-celebrate).
//
// Also detects XP increases between polls and surfaces a small "+N XP"
// toast so users feel the loyalty loop in real time.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/api";

const LoyaltyContext = createContext({
    status: null,
    refresh: () => {},
    celebrate: false,
    dismissCelebrate: () => {},
});

const SEEN_KEY = "wyng_second_wing_seen_v1";

export function LoyaltyProvider({ children }) {
    const [status, setStatus] = useState(null);
    const [celebrate, setCelebrate] = useState(false);
    const prevWings = useRef(null);
    const prevXp = useRef(null);
    const firstLoad = useRef(true);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/loyalty/status");
            const next = r.data;
            setStatus(next);

            // Celebrate when crossing 1 → 2, but only ONCE per user/account.
            // Hard rules to avoid the celebration mis-firing on cold mounts
            // (which used to swallow the user's first tap on deep-linked
            // routes — see /app/test_reports/iteration_32.json):
            //   1. localStorage SEEN_KEY must be empty.
            //   2. We must have OBSERVED the 1 → 2 transition in this
            //      session (prevWings.current === 1 right before now).
            //      Cold mounts where wings is already 2 do NOT re-fire.
            const seenAt = localStorage.getItem(SEEN_KEY);
            const wasOne = prevWings.current === 1;
            const earnedAt = next?.second_wing?.earned_at;
            if (next?.wings === 2 && earnedAt && !seenAt && wasOne) {
                setCelebrate(true);
            }
            // If we land cold and the wing is already 2, mark it as seen
            // so the celebration never fires on a future cold mount —
            // those users have either seen it elsewhere or are returning.
            if (next?.wings === 2 && earnedAt && !seenAt && firstLoad.current) {
                try { localStorage.setItem(SEEN_KEY, earnedAt); } catch {}
            }
            prevWings.current = next?.wings ?? null;

            // XP-earn toast: only fire when xp grew between polls. Skip on
            // the very first load so we don't toast historical XP.
            const nextXp = Number(next?.xp_total || 0);
            if (!firstLoad.current && prevXp.current !== null && nextXp > prevXp.current) {
                const delta = nextXp - prevXp.current;
                if (delta > 0 && delta < 1000) {
                    toast.success(`+${delta} XP verdiend`, {
                        duration: 2200,
                        className: "wyng-xp-toast",
                    });
                }
            }
            prevXp.current = nextXp;
            firstLoad.current = false;
        } catch {
            /* ignore — non-critical */
        }
    }, []);

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 90_000);
        const onFocus = () => refresh();
        const onXpEvent = () => refresh();
        window.addEventListener("focus", onFocus);
        // Pages can dispatch this after a successful XP-earning action
        // (chat send, meal log, …) so the +XP toast fires within ~1s
        // instead of waiting for the next 90s poll.
        window.addEventListener("wyng:xp-event", onXpEvent);
        return () => {
            clearInterval(t);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("wyng:xp-event", onXpEvent);
        };
    }, [refresh]);

    const dismissCelebrate = useCallback(() => {
        try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch {}
        setCelebrate(false);
    }, []);

    return (
        <LoyaltyContext.Provider value={{ status, refresh, celebrate, dismissCelebrate }}>
            {children}
        </LoyaltyContext.Provider>
    );
}

export function useLoyalty() {
    return useContext(LoyaltyContext);
}
